import {
  AnalyticsComponent,
  AnalyticsFieldName,
} from "@/constants/analytics/Analytics";
import {
  ScheduleAnalyticsEvent,
  ScheduleAnalyticsSources,
} from "@/constants/analytics/Scheduling";
import {
  CTAText,
  EventCancellationError,
  EventCancellationText,
} from "@/constants/Schedule";
import { ConfirmationAlertTestID } from "@/constants/TestID";
import useScheduleQueryPatientId from "@/hooks/useScheduleQueryPatientId";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { useCancelGroupClassEventMutation } from "@/store/api/schedule/schedule";
import ConfirmationAlert from "@/ui/composed/alerts/ConfirmationAlert";
import { FAIconName } from "@/ui/core";

interface DeclineGroupEventConfirmationAlertProps {
  eventTitle: string;
  groupClassExternalId: string;
  isOpen: boolean;
  onCompletion: () => void;
  onDismiss: () => void;
  patientExternalId: string;
}

const DeclineGroupEventConfirmationAlert = ({
  eventTitle,
  groupClassExternalId,
  isOpen,
  onCompletion,
  onDismiss,
  patientExternalId: patientIdArg,
}: DeclineGroupEventConfirmationAlertProps) => {
  const { captureException } = useAppMonitor();
  const { track } = useAnalytics();
  const [cancelGroupClassEvent] = useCancelGroupClassEventMutation();
  const patientExternalId = useScheduleQueryPatientId(patientIdArg);

  const handleDeclineEventConfirm = () => {
    track(ScheduleAnalyticsEvent.GroupEventDetailsDecline, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.EventId]: groupClassExternalId,
      [AnalyticsFieldName.EventTitle]: eventTitle,
      [AnalyticsFieldName.Source]:
        ScheduleAnalyticsSources.GroupEventDetailView,
    });

    cancelGroupClassEvent({
      groupClassEventExternalId: groupClassExternalId,
      patientExternalId,
    })
      .then(() => onCompletion())
      .catch((error) => {
        captureException(
          new Error(EventCancellationError.DecliningGroupClassEventError, {
            cause: error,
          }),
        );
      });
  };

  return (
    <ConfirmationAlert
      confirmationType="destructive"
      confirmLeftIcon={FAIconName.Trash}
      confirmText={CTAText.DeclineEvent}
      isOpen={isOpen}
      message={EventCancellationText.DeclineGroupClassEventAlertMessage}
      onConfirm={handleDeclineEventConfirm}
      onDismiss={onDismiss}
      testID={ConfirmationAlertTestID.DeclineGroup}
      title={EventCancellationText.DeclineGroupClassEventAlertTitle}
    />
  );
};

export default DeclineGroupEventConfirmationAlert;
